<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';
import { useSidebarContext } from './provider';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  // Optional direct count for expandable headers when collapsed
  headerCount: { type: Number, default: 0 },
  // Direct count for non-expandable items (like VIP, Unattended)
  count: { type: Number, default: 0 },
});

const emit = defineEmits(['toggle']);
const { isReportsSidebar, isConversationSidebar } = useSidebarContext();

const shouldShowLeadingIcon = computed(() => {
  if (!props.icon) return false;
  if (!isConversationSidebar?.value) return true;
  // Conversation-only: keep icons for Channels & Labels, remove others
  return props.name === 'Channels' || props.name === 'Labels';
});

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const formattedDynamicCount = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
// Format direct count prop
const formattedCount = computed(() => {
  if (!props.count) return '';
  return props.count > 99 ? '99+' : props.count;
});
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2 px-2 py-1.5 rounded-lg h-8 min-w-0 wlab-sidebar-header"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'bg-n-alpha-2 font-medium': isActive && !hasActiveChild,
      'font-medium': hasActiveChild,
      'hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="shouldShowLeadingIcon" class="relative flex items-center gap-2">
      <Icon v-if="shouldShowLeadingIcon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0">
      <span class="text-sm font-medium leading-5 truncate">
        {{ label }}
      </span>
      <!-- Non-expandable items: show dynamicCount (via getterKeys) or direct count prop -->
      <span
        v-if="dynamicCount && !expandable"
        class="wlab-sidebar-header-count rounded-md capitalize text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0"
        :class="{
          'text-n-blue-text outline-n-slate-6': isActive,
          'text-n-slate-11 outline-n-strong': !isActive,
        }"
      >
        {{ formattedDynamicCount }}
      </span>
      <!-- Non-expandable items with direct count prop (like VIP, Unattended) -->
      <span
        v-else-if="!expandable && formattedCount"
        class="wlab-sidebar-header-count rounded-md capitalize text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0"
        :class="{
          'text-n-blue-text outline-n-slate-6': isActive,
          'text-n-slate-11 outline-n-strong': !isActive,
        }"
      >
        {{ formattedCount }}
      </span>
      <!-- Expandable headers show provided headerCount only when collapsed -->
      <span
        v-else-if="expandable && !isExpanded && headerCount > 0"
        class="wlab-sidebar-header-count rounded-md capitalize text-xs leading-5 font-medium text-center outline outline-1 px-1 flex-shrink-0 text-n-slate-11 outline-n-strong"
      >
        {{ headerCount > 99 ? '99+' : headerCount }}
      </span>
    </div>
    <span
      v-if="expandable && isReportsSidebar"
      class="i-lucide-chevron-right size-3 flex-shrink-0 transition-transform duration-150"
      :class="{ 'rotate-90': isExpanded }"
      @click.stop="emit('toggle')"
    />
    <!-- Show chevron up when expanded, chevron down when collapsed -->
    <span
      v-else-if="expandable && isExpanded"
      class="i-lucide-chevron-up size-3 flex-shrink-0"
      @click.stop="emit('toggle')"
    />
    <span
      v-else-if="expandable && !isExpanded"
      class="i-lucide-chevron-down size-3 flex-shrink-0"
      @click.stop="emit('toggle')"
    />
  </component>
</template>

<style>
.wlab-sidebar-header,
.wlab-sidebar-header *,
.wlab-sidebar-header span,
.wlab-sidebar-header .text-n-slate-11,
.wlab-sidebar-header .text-n-blue-text,
.wlab-sidebar-header .text-xs,
.wlab-sidebar-header .text-sm {
  font-family: 'Excon', sans-serif !important;
  font-size: 11px !important;
}

/* Light mode - Black text */
body:not(.dark) .wlab-sidebar-header,
body:not(.dark) .wlab-sidebar-header * {
  color: #000000 !important;
}

/* Dark mode - White text */
body.dark .wlab-sidebar-header,
body.dark .wlab-sidebar-header * {
  color: #ffffff !important;
}

/* ===== HOVER ONLY - Orange ===== */

/* Light mode - Orange ONLY on hover */
body:not(.dark) .wlab-sidebar-header:hover:not(.bg-n-alpha-2) {
  background: rgba(0, 102, 255, 0.12) !important;
}

body:not(.dark) .wlab-sidebar-header:hover * {
  color: #004db3 !important;
}

/* Dark mode - Orange ONLY on hover */
body.dark .wlab-sidebar-header:hover:not(.bg-n-alpha-2) {
  background: rgba(51, 136, 255, 0.15) !important;
}

body.dark .wlab-sidebar-header:hover * {
  color: #66a3ff !important;
}

/* Ensure header-count badge is full 11px */
.wlab-sidebar-header-count {
  font-size: 11px !important;
  line-height: 1.2 !important;
}

/* Conversation sidebar: count badges at 9px to match leaf items */
.wlab-sidebar--conversation .wlab-sidebar-header-count {
  font-size: 9px !important;
  line-height: 1.1 !important;
  padding: 1px 4px !important;
}
</style>
